console.log('Vite HMR status:', import.meta.hot ? 'ACTIVE' : 'INACTIVE');

import rootReducer from './reducers/index.reducer';
import { configureStore } from '@reduxjs/toolkit';

let createdStore = null;

/**
 *
 */
export const PERSISTENCE_VERSION = 2;

const PERSISTED_USER_FIELDS = [
  'userInfo',
  'userId',
  'userLanguage',
  'objectViewerType',
  'mobileViewWidth',
  'isMobileView',
  'defaultFileTypes',
  'hideInstallButton',
  'isIframe',
  'customizeData',
  'GimUnitsData',
  'zoomInLimit',
];

/**
 * @param {unknown} value
 * @returns {value is Record<string, unknown>}
 */
const isRecord = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

/**
 * Select only non-sensitive identity and UI state for durable storage.
 * Tokens and authentication lifecycle state intentionally stay in memory.
 * @param {Partial<import('global').UserState>} userState
 * @returns {Partial<import('global').UserState>}
 */
export const selectPersistedUserState = (userState = {}) => {
  if (!isRecord(userState)) return {};

  return PERSISTED_USER_FIELDS.reduce((persistedUser, field) => {
    if (userState[field] !== undefined) persistedUser[field] = userState[field];
    return persistedUser;
  }, {});
};

/**
 * Select the durable Redux projection. Auth state is never included.
 * @param {Record<string, any>} state
 * @returns {{version: number, user: Record<string, any>}}
 */
export const selectPersistedState = (
  /** @type {import('global').ReduxAppState} */ state
) => ({
  version: PERSISTENCE_VERSION,
  user: selectPersistedUserState(state?.user),
});

/**
 * Safely migrate both versioned and legacy reduxState shapes.
 * Authentication state is deliberately discarded during every migration.
 * @param {unknown} persistedState
 * @param {import('global').ReduxAppState} defaultState
 * @returns {import('global').ReduxAppState}
 */
export const migratePersistedState = (persistedState, defaultState) => {
  const state = defaultState ?? rootReducer(undefined, { type: '@@INIT' });
  const persistedRecord = isRecord(persistedState) ? persistedState : {};
  const persistedUser = isRecord(persistedRecord.user)
    ? persistedRecord.user
    : persistedRecord;

  return {
    ...state,
    user: {
      ...state.user,
      ...selectPersistedUserState(persistedUser),
    },
  };
};

/**
 * Store factory. Returns a single shared store instance so that the Provider
 * (main.jsx), the axios interceptors (axiosConfig.jsx) and action files all
 * read and dispatch against the same Redux state.
 * @param {import('global').ReduxAppState} [initialState] optional preloaded
 * application state
 * @returns {import('@reduxjs/toolkit').EnhancedStore}
 */
export default (initialState) => {
  if (createdStore) return createdStore;

  const defaultState =
    initialState ?? rootReducer(undefined, { type: '@@INIT' });

  //1. Load saved state from localStorage (if it exists)
  const loadState = () => {
    try {
      const serializedState = localStorage.getItem('reduxState');
      if (serializedState === null) return defaultState;
      return migratePersistedState(JSON.parse(serializedState), defaultState);
    } catch (err) {
      console.warn('Failed to load state from localStorage', err);
      return defaultState;
    }
  };

  // 2. We Create store with preloaded state
  const store = configureStore({
    reducer: rootReducer,
    preloadedState: loadState(), // persisting state
  });

  // 3. Save only the safe projection, including immediately after migration.
  const persistState = () => {
    try {
      localStorage.setItem(
        'reduxState',
        JSON.stringify(selectPersistedState(store.getState()))
      );
    } catch (err) {
      console.warn('Failed to save state to localStorage', err);
    }
  };
  persistState();
  store.subscribe(persistState);

  if (import.meta.hot) {
    console.log('>>> HMR update triggered'); // Check here

    const modules = import.meta.glob('./reducers/index.reducer.jsx');

    import.meta.hot.accept(['./reducers/index.reducer.jsx'], async () => {
      /** @type {{default: any}} **/ const newModule =
        await modules['./reducers/index.reducer.jsx']();
      store.replaceReducer(newModule.default);
    });
  }

  createdStore = store;

  return store;
};
